import { useCallback } from 'react';
import useEntitlementsStore from 'Store/useEntitlementsStore';

/**
 * useHasAccess
 *
 * Returns a memoized `hasAccess(resource, requiredEntitlements)` predicate
 * that checks the *current role's* entitlements for a given resource + action.
 *
 * `entitlements` in useEntitlementsStore is always kept in sync with the active role:
 *   - On login  → set to entitlementsData[0].resources (first role)
 *   - On switch → set to the selected role's resources  (via setEntitlements)
 *
 * This means hasAccess automatically reflects role switches without any
 * extra wiring — it just depends on the single `entitlements` array.
 *
 * Shape of entitlements:
 *   [{ name: 'jobs_library', entitlements: ['view', 'create', ...] }, ...]
 *
 * @example
 *   const { hasAccess } = useHasAccess();
 *   hasAccess('jobs_library', ['view'])          // → true
 *   hasAccess('people_plus_ai', ['inprogress'])  // → true/false
 *   hasAccess('unknown_resource', ['view'])      // → false
 */
export const useHasAccess = () => {
  const entitlements = useEntitlementsStore((s) => s.entitlements);

  const hasAccess = useCallback(
    (resource, requiredEntitlements = []) => {
      // No resource defined → open to any authenticated user
      if (!resource) return true;

      // Entitlements not loaded yet
      if (!Array.isArray(entitlements) || entitlements.length === 0) return false;

      const resourceEntry = entitlements.find(ent => ent.name === resource);
      if (!resourceEntry) return false;

      const userEntitlements = resourceEntry.entitlements ?? [];

      // Wildcard '*' grants all actions
      if (userEntitlements.includes('*')) return true;

      return requiredEntitlements.some(action => userEntitlements.includes(action));
    },
    [entitlements],
  );

  return { hasAccess };
};
